import { usePreviewContext } from '../../../contexts/PreviewContext.ts';
import { useReportStore } from '@store';
import {
  useCancelReportGeneratingMutation,
  useGeneratePatientReportMutation,
} from '@common/cache';
import { useEffect, useState } from 'react';
import { Box, capitalize, SelectChangeEvent } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { type Id as ToastId, toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { FormatedReportType } from '@views/pages/Reporting/Reports/types.ts';
import { paths, ReportType } from '@common/types';
import { FormattedToastMessage } from '@components';
import { t } from 'i18next';
import { useDownloadsNotification } from '@common/hooks';
import { debounce } from '@util/debounce.ts';

const formattedReportType: Record<ReportType, FormatedReportType> = {
  records_report: 'record',
  patients_report: 'patients',
  tasks_report: 'tasks',
  users_report: 'users',
};

let toastId: ToastId;
export const useFilters = () => {
  const {
    handleFilterChange,
    handleToggleFiltersCard,
    filters,
    isFiltersCardOpen,
  } = usePreviewContext();
  const { event } = useDownloadsNotification();
  const { report, introspection } = useReportStore();
  const [searchQuery, setSearchQuery] = useState(filters.search);

  const { pathname } = useLocation();
  const isItemsView = pathname.includes('/reporting/items');

  const { mutateAsync: reportMutateAsync, isPending } =
    useGeneratePatientReportMutation();

  const { mutateAsync: cancelAsync, isPending: isCanceling } =
    useCancelReportGeneratingMutation();

  const handleCancel = async (report_id: string) => {
    if (isCanceling) return;

    try {
      await cancelAsync(report_id);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDownloadReportCSV = async () => {
    if (!report) return;

    try {
      const response = await reportMutateAsync({
        report_id: report.id || '',
        report_type:
          formattedReportType[report.report_type || 'patients_report'],
        props: {
          ...(filters.group_by && {
            group_by: filters.group_by,
          }),
          ...(filters.sort_by && {
            sort_by: filters.sort_by,
          }),
          ...(filters?.options && {
            options: filters.options,
          }),
          ...(filters?.search && {
            search: filters.search,
          }),
          ...(filters?.interval && {
            interval: filters.interval,
          }),
          ...(filters?.custom_timeframe && {
            custom_timeframe: filters.custom_timeframe,
          }),
          ...(filters?.page && {
            page: filters.page,
          }),
          ...(filters?.search && {
            search: filters.search,
          }),
        } as paths['/reporting/patients/reports/{report_id}/csv/request']['post']['requestBody']['content']['application/json'],
      });

      toastId = toast.info(
        <FormattedToastMessage
          title={t(
            `reporting.report.preview.generatingReport.title.${response.status}`,
          )}
          message={t(
            `reporting.report.preview.generatingReport.message.${response.status}`,
          )}
        />,
        {
          closeButton:
            response.status === 201 ? (
              <Box
                onClick={() => handleCancel(response.data.id)}
                sx={{ width: '60px' }}
              >
                Cancel
              </Box>
            ) : (
              <></>
            ),
          autoClose: false,
        },
      );
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debounce(
      () => {
        handleFilterChange({ search: e.target.value });
      },
      true,
      500,
    );
  };

  const handleGroupByChange = (e: SelectChangeEvent<string>) => {
    handleFilterChange({ group_by: e.target.value });
  };

  const groupByOptions =
    introspection?.groupable_by?.map(group => ({
      value: group.field,
      label: group.label,
    })) ?? [];

  const searchPlaceholder = capitalize(
    report?.report_type?.split('_report')[0] ?? '',
  );

  useEffect(() => {
    if (event?.type === 'report-download.completed') {
      toast.update(toastId, { autoClose: 1 });
    }
  }, [event]);

  useEffect(() => {
    return () => {
      toast.update(toastId, { autoClose: 1 });
    };
  }, []);

  useEffect(() => {
    setSearchQuery(filters.search);
  }, [filters.search]);

  return {
    filters,
    groupByOptions,
    handleGroupByChange,
    handleSearchChange,
    handleToggleFiltersCard,
    handleDownloadReportCSV,
    isFileDownloading: isPending,
    isItemsView,
    showDownloadButton: report?.id && isItemsView,
    searchPlaceholder,
    searchQuery,
    isFiltersCardOpen,
  };
};
